import Link from 'next/link'

import Icon from '@/legacy-src/components/icons/Icon'
import type { TherapistType } from '@/legacy-src/types/TherapistStateType'
import { getTranslate } from '@/legacy-src/utilities/i18n'

import { Heading } from '@/src/domains/design-system/components/Heading'
import { Text } from '@/src/domains/design-system/components/Text'
import { useIsClientOfflineBookingAvailable } from '@/src/domains/client-booking/hooks/useIsClientOfflineBookingAvailable'
import { getGoogleMapsHref } from '@/src/domains/google-maps/utils/getGoogleMapsHref'

import { RecommendedTherapistProfileBioSessionRolldown } from './RecommendedTherapistProfileBioSessionRolldown'

export interface RecommendedTherapistProfileBioSessionProps {
  therapist: Pick<TherapistType, 'hasOfflineTherapy' | 'office'>
}

export const RecommendedTherapistProfileBioSession = ({ therapist }: RecommendedTherapistProfileBioSessionProps) => {
  const { t } = getTranslate()

  const offlineTherapyAvailable = useIsClientOfflineBookingAvailable({ therapist })

  return (
    <section className="flex flex-col gap-4">
      <Heading element="h3" variant="2xl">
        {t('component.therapistProfile.session.title')}
      </Heading>
      <ul className="m-0 flex flex-col rounded-2xl bg-white-500 p-5 text-petrol-500">
        <li className="flex list-none gap-4">
          <Icon icon="video" size={24} className="shrink-0" />
          <div className="flex flex-col gap-2">
            <Text element="h4" weight="bold">
              {t('component.therapistProfile.session.online.title')}
            </Text>
            <Text element="p" variant="sm">
              {t('component.therapistProfile.session.online.description')}
            </Text>
          </div>
        </li>
        <>
          {offlineTherapyAvailable ? (
            <li className="mt-5 flex list-none gap-4 border-0 border-t-2 border-solid border-sand-500 pt-5 text-petrol-500">
              <Icon icon="location" size={24} className="shrink-0" />
              <div className="flex flex-col gap-2">
                <Text element="h4" weight="bold">
                  {t('component.therapistProfile.session.offline.title')}
                </Text>

                <Text element="address" variant="sm">
                  {therapist.office!.location.address}{' '}
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary"
                    href={getGoogleMapsHref(therapist.office!.location.address)}
                  >
                    (map)
                  </Link>
                </Text>
                <RecommendedTherapistProfileBioSessionRolldown office={therapist.office!} />
              </div>
            </li>
          ) : null}
        </>
      </ul>
    </section>
  )
}
